import React from "react";
import { fmt } from "../lib/framing";

const RULE_LABEL = {
  equal: "Equal borders",
  weighted_bottom: "Weighted bottom",
  golden: "Golden ratio",
  custom: "Custom",
};
const LAYER_LABEL = { 0: "None", 1: "Single", 2: "Double", 3: "Triple" };

const Row = ({ label, value, unit, strong }) => (
  <div className={`flex items-center justify-between border-b py-1.5 ${strong ? "border-slate-900" : "border-slate-300"}`}>
    <span className={`text-[13px] ${strong ? "font-bold text-slate-900" : "text-slate-600"}`}>{label}</span>
    <span className={`font-mono text-[13px] ${strong ? "font-bold" : "text-slate-900"}`}>
      {value} <span className="text-[11px] text-slate-500">{unit}</span>
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="mb-1 mt-4 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{children}</p>
);

export const CutSheet = ({ r, unit, reference }) => {
  if (!r) return null;
  const mats = r.mat_cuts || [];
  const date = new Date().toLocaleString();

  return (
    <div className="print-cut-sheet">
      <div className="mx-auto max-w-3xl bg-white p-10 text-slate-900">
        <div className="mb-6 flex items-end justify-between border-b-2 border-slate-900 pb-3">
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Framer's Bench</h1>
            <p className="text-sm text-slate-500">Frame &amp; Mat Cut Sheet</p>
          </div>
          <div className="text-right text-[12px] text-slate-500">
            {reference ? <p className="text-sm font-semibold text-slate-900">{reference}</p> : null}
            <p>{date}</p>
            <p>Units: {unit === "in" ? "inches" : "millimetres"}</p>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap gap-x-8 gap-y-1 text-[12px] text-slate-600">
          <span>Solving: <b className="text-slate-900">{r.mode === "solve_frame" ? "Frame from artwork" : "Artwork from frame"}</b></span>
          <span>Mat rule: <b className="text-slate-900">{RULE_LABEL[r.rule]}</b></span>
          <span>Mat layers: <b className="text-slate-900">{LAYER_LABEL[r.mat_layers]}</b></span>
          {r.mat_layers >= 2 && <span>Under reveal: <b className="text-slate-900">{fmt(r.under_mat_reveal, unit)} {unit}</b></span>}
          {r.mat_layers >= 3 && <span>3rd reveal: <b className="text-slate-900">{fmt(r.third_mat_reveal, unit)} {unit}</b></span>}
        </div>

        <div className="grid grid-cols-2 gap-x-10">
          <div>
            <SectionTitle>Artwork</SectionTitle>
            <Row label="Width × Height" value={`${fmt(r.painting_width, unit)} × ${fmt(r.painting_height, unit)}`} unit={unit} />
            {r.painting_depth > 0 && <Row label="Depth" value={fmt(r.painting_depth, unit)} unit={unit} />}

            <SectionTitle>Visible mat reveals</SectionTitle>
            <Row label="Top" value={fmt(r.mat_top, unit)} unit={unit} />
            <Row label="Bottom" value={fmt(r.mat_bottom, unit)} unit={unit} />
            <Row label="Left" value={fmt(r.mat_left, unit)} unit={unit} />
            <Row label="Right" value={fmt(r.mat_right, unit)} unit={unit} />

            <SectionTitle>Moulding</SectionTitle>
            <Row label="Face width" value={fmt(r.moulding_face_width, unit)} unit={unit} />
            <Row label="Rebate width" value={fmt(r.rebate_width, unit)} unit={unit} />
            {r.rebate_depth > 0 && <Row label="Rebate depth" value={fmt(r.rebate_depth, unit)} unit={unit} />}
          </div>

          <div>
            <SectionTitle>Cut sizes</SectionTitle>
            <Row label="Outer frame (mitred 45°, 2 each)" value={`${fmt(r.outer_width, unit)} × ${fmt(r.outer_height, unit)}`} unit={unit} />
            <Row label="Visible opening" value={`${fmt(r.visible_opening_width, unit)} × ${fmt(r.visible_opening_height, unit)}`} unit={unit} />
            {r.spacer_type && r.spacer_type !== "none" && (r.spacer_type === "matboard" || r.spacer_type === "foam") && (
              <>
                <Row label="Spacer outer" value={`${fmt(r.spacer_outer_width, unit)} × ${fmt(r.spacer_outer_height, unit)}`} unit={unit} />
                <Row label="Spacer inner" value={`${fmt(r.spacer_inner_width, unit)} × ${fmt(r.spacer_inner_height, unit)}`} unit={unit} />
              </>
            )}
            {r.spacer_type === "plastic" && (
              <>
                <Row label="Spacer top/bottom (×2)" value={fmt(r.spacer_tb_length, unit)} unit={unit} />
                <Row label="Spacer left/right (×2)" value={fmt(r.spacer_lr_length, unit)} unit={unit} />
              </>
            )}
            {r.spacer_type === "wood" && (
              <Row label="Spacer lengths (mitred, ×2 each)" value={`${fmt(r.spacer_wood_length_a, unit)} × ${fmt(r.spacer_wood_length_b, unit)}`} unit={unit} />
            )}
            <Row label="Glass / acrylic" value={`${fmt(r.glass_cut_width, unit)} × ${fmt(r.glass_cut_height, unit)}`} unit={unit} />
            <Row label="Matboard (to rebate)" value={`${fmt(r.total_mat_width, unit)} × ${fmt(r.total_mat_height, unit)}`} unit={unit} />
            <Row label="Backing (to rebate)" value={`${fmt(r.backing_cut_width, unit)} × ${fmt(r.backing_cut_height, unit)}`} unit={unit} />

            {mats.length > 0 && (
              <>
                <SectionTitle>Mat window cut sizes</SectionTitle>
                {mats.map((m) => (
                  <Row key={m.index} label={`${m.name} window`} value={`${fmt(m.window_width, unit)} × ${fmt(m.window_height, unit)}`} unit={unit} />
                ))}

                <SectionTitle>Window cuts (matboard edge → cut line)</SectionTitle>
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="py-1 text-left font-semibold"></th>
                      {mats.map((m) => <th key={m.index} className="py-1 text-right font-semibold">{m.name}</th>)}
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {["top", "bottom", "left", "right"].map((side) => (
                      <tr key={side} className="border-t border-slate-200">
                        <td className="py-1 font-sans capitalize text-slate-600">{side}</td>
                        {mats.map((m) => <td key={m.index} className="py-1 text-right">{fmt(m[`cut_${side}`], unit)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        {r.depth_calc && (r.depth_layers || []).length > 0 && (
          <div className="mt-2">
            <SectionTitle>Layer depths (front → back)</SectionTitle>
            <div className="grid grid-cols-2 gap-x-10">
              <div>
                {(r.depth_layers || []).map((l, i) => <Row key={i} label={l.name} value={fmt(l.depth, unit)} unit={unit} />)}
              </div>
              <div>
                <Row label="Total stack depth" value={fmt(r.total_depth, unit)} unit={unit} />
                {r.rebate_depth > 0 && <Row label="Rebate depth" value={fmt(r.rebate_depth, unit)} unit={unit} />}
                {r.rebate_depth > 0 && (
                  <p className={`mt-1 text-[12px] font-semibold ${r.depth_fits ? "text-emerald-700" : "text-red-600"}`}>
                    {r.depth_fits ? "✓ Contents fit the rebate" : "✗ Contents exceed the rebate depth"}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 border-t-2 border-slate-900 pt-3">
          <Row label="OUTER FRAME SIZE" value={`${fmt(r.outer_width, unit)} × ${fmt(r.outer_height, unit)}`} unit={unit} strong />
        </div>

        <p className="mt-8 text-center text-[10px] text-slate-400">
          Generated by Framer's Bench · Border cuts measured from the matboard edge unless noted.
        </p>
      </div>
    </div>
  );
};
